import { useTranslation } from 'react-i18next';
import ServiceTypes, { types } from '../../../service/ServiceTypes';
import AttToken from '../../../helpers/attToken';
import { toast } from 'react-toastify';
import queryClient from '../../../service/query';
import NovaNotificacao, { newNotificationType } from '../../../service/NovaNotificacao';
import { Autocomplete, Skeleton, TextField } from '@mui/material';
import { trackEventMatomo } from '../../../helpers/matomo';
import HeaderModal from '../../HeaderModal/HeaderModal';
import FooterModal from '../../FooterModal/FooterModal';
import { useEffect } from 'react';
import QueryFn from '../../../helpers/QueryFn';

const queryFn = new QueryFn();

type props = {
  isEdit: boolean,
  notificationEdit: newNotificationType | undefined,
  setNotificationEdit: Function,
  openModal: boolean,
  setOpenModal: Function,
};

const serviceType = new ServiceTypes();
const notifService = new NovaNotificacao();

export default function NewNotificationsModal({
  isEdit,
  notificationEdit,
  setNotificationEdit,
  openModal,
  setOpenModal }: props) {
  const { t } = useTranslation('translation');

  const response = queryFn.useFetch('SERVICES_LIST',
    async () => {
      try {
        const token = await AttToken();
        if (token) {
          const body = { ...token, empresa: token.userEmpresa };
          const result = await serviceType.getAllServices(body);
          return result;
        }
      } catch (err) {
        return err;
      }
    }
  );

  const create = queryFn.useMutate('NEW_NOTIFICATION_MODEL',
    async () => {
      toast.warn(t('notifications.newModificationModal.creatingNewNotificationModel') as string);
      try {
        const token = await AttToken();
        if (token) {
          const response = await notifService.create(token, notificationEdit as newNotificationType);
          queryClient.resetQueries({ queryKey: ['GET_NOTIFICATION_LIST'] });
          setOpenModal(!openModal);
          toast.success(`${t('notifications.ticket.ModeloCriado')}`);
          return response;
        }
      } catch (err) {
        toast.error(`${t('notifications.ticket.ErroAoCriarModelo')}`);
        console.log(err);
      }
    }
  );

  const edit = queryFn.useMutate('EDIT_NOTIF',
    async () => {
      toast.warn(t('notifications.newModificationModal.editingNotification') as string);
      try {
        const token = await AttToken();
        if (token) {
          const response = await notifService.edit(token, notificationEdit as newNotificationType);
          setOpenModal(!openModal);
          queryClient.resetQueries({ queryKey: ['GET_NOTIFICATION_LIST'] });
          toast.success(t('notifications.newModificationModal.notificationChanged') as string);
          return response;
        }
      } catch (err) {
        toast.error(t('notifications.newModificationModal.errorChangingNotification') as string);
      }
    }
  );

  const close = () => {
    trackEventMatomo('modal criação de notificação', 'click', 'button', 'fecha modal');
    setOpenModal(!openModal);
  };

  const submit = () => {
    trackEventMatomo('modal criação de notificação', 'click', 'button', 'salva');
    isEdit ? edit.mutateAsync('') : create.mutateAsync('');
  };

  useEffect(() => {
  }, [notificationEdit]);

  const escape = (e: any) => {
    if (e.key === 'Escape') {
      close();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', escape, true);
  }, [escape]);

  return (
    <div className={`modal ${openModal ? "modal is-active" : "modal"}`}>
      <div className="modal-background" onClick={() => close()} onKeyDown={(e) => escape(e)} />
      <div className="modal-card">
        <HeaderModal
          headerTitle={!isEdit
            ? t('notifications.ticket.criarModeloNot')
            : t('notifications.ticket.editarModeloNoti')}
          close={close}
        />
        <section className="modal-card-body" style={{ textAlign: 'initial' }}>
          <TextField
            size='small'
            onClick={() => trackEventMatomo('modal criação de notificação', 'click', 'input', 'edita valor')}
            fullWidth
            style={{ marginBottom: '10px' }}
            label={t('name')}
            value={notificationEdit ? notificationEdit?.name : undefined}
            onChange={(event) => setNotificationEdit({ ...notificationEdit, name: event.target.value })}
          />
          <TextField
            size='small'
            onClick={() => trackEventMatomo('modal criação de notificação', 'click', 'input', 'edita valor')}
            multiline
            fullWidth
            style={{ marginBottom: '10px' }}
            rows={2}
            maxRows={4}
            value={notificationEdit?.message}
            label={t('message')}
            onChange={(event) => setNotificationEdit({ ...notificationEdit, message: event.target.value })}
          />
          {response.isLoading ? <Skeleton className='column' /> :
            <Autocomplete
              size='small'
              onClick={() => trackEventMatomo('modal criação de notificação', 'click', 'select', 'seleciona valor')}
              id="combo-box-demo"
              sx={{ marginTop: '5px' }}
              defaultValue={notificationEdit?.serviceType ? {
                value: notificationEdit?.serviceType as string,
                label: response.data?.find((ele: types) => ele.tipoId === notificationEdit?.serviceType)?.nome
              } : null}
              filterSelectedOptions
              options={
                response.data
                  ?.sort((a: types, b: types) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : - 1; })
                  ?.map((ele: any) => ({
                    label: ele.nome,
                    value: ele.tipoId,
                  }))}
              onChange={(event: any, newValue: { label: string, value: string } | null) => {
                setNotificationEdit({ ...notificationEdit, serviceType: newValue?.value });
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <span>{option.label}</span>
                </li>)}
              renderInput={(params) => <TextField {...params} label={t('notifications.ticket.tipoServico')} />}
            />
          }
        </section>
        <FooterModal
          label={`${t('save')}`}
          disabled={
            (!notificationEdit?.name?.length) ||
            (!notificationEdit?.message?.length) ||
            (!notificationEdit?.serviceType?.length)
          }
          submit={submit}
        />
      </div>
    </div>
  );
}
